import { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import type { CartelaDados } from '../types';
import { carregarImagem, montarCartela } from '@/lib/cartela';

interface Props {
  rifaId: number;
  vendedorId: number | null;
}

type Estado = 'preparando' | 'pronto' | 'erro' | 'semArte';

export default function CartelaVendedor({ rifaId, vendedorId }: Props) {
  const [estado, setEstado] = useState<Estado>('preparando');
  const [dados, setDados] = useState<CartelaDados | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [mandando, setMandando] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  const qrRef = useRef<HTMLCanvasElement | null>(null);
  const urlRef = useRef<string | null>(null);

  // Limpa a URL de objeto ao desmontar.
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  // Busca os dados da cartela (RPC) sempre que rifa/vendedor mudam.
  useEffect(() => {
    let ativo = true;
    setEstado('preparando');
    setErro(null);
    setDados(null);
    setBlob(null);
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
      setBlobUrl(null);
    }

    (async () => {
      try {
        const { data, error } = await supabase.rpc('cartela_do_vendedor', {
          p_rifa: rifaId,
          p_vendedor: vendedorId,
        });
        if (error) throw error;
        const c = (data as CartelaDados | null) ?? null;
        if (!ativo) return;
        if (!c || !c.arte_url || !c.link) {
          setEstado('semArte');
          return;
        }
        setDados(c);
      } catch (e) {
        if (ativo) {
          setEstado('erro');
          setErro(mensagemAmigavel(e, 'Não consegui preparar sua cartela.'));
        }
      }
    })();

    return () => {
      ativo = false;
    };
  }, [rifaId, vendedorId, tentativa]);

  // Monta a imagem quando os dados (e o QR) estiverem prontos.
  useEffect(() => {
    if (!dados || !dados.arte_url || !dados.link) return;
    let ativo = true;

    (async () => {
      try {
        // Espera o QRCodeCanvas desenhar.
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
        const qr = qrRef.current;
        if (!qr) throw new Error('QR não gerado.');

        const img = await carregarImagem(dados.arte_url!);
        if (!ativo) return;
        const novoBlob = await montarCartela(img, qr, dados);
        if (!ativo) return;
        urlRef.current = URL.createObjectURL(novoBlob);
        setBlob(novoBlob);
        setBlobUrl(urlRef.current);
        setEstado('pronto');
      } catch (e) {
        if (ativo) {
          setEstado('erro');
          setErro(mensagemAmigavel(e, 'Não consegui montar sua cartela.'));
        }
      }
    })();

    return () => {
      ativo = false;
    };
  }, [dados]);

  function tentarDeNovo() {
    setTentativa((v) => v + 1);
  }

  function baixar() {
    if (!blob || !dados) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = dados.arquivo || 'cartela.jpg';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function mandarWhats() {
    if (!blob || !dados) return;
    setMandando(true);
    setAviso(null);
    try {
      const arquivo = new File([blob], dados.arquivo || 'cartela.jpg', { type: 'image/jpeg' });
      const podeCompartilhar =
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [arquivo] });

      if (podeCompartilhar) {
        await navigator.share({ files: [arquivo], text: dados.mensagem });
      } else {
        // Baixa a imagem e abre o WhatsApp só com a mensagem.
        baixar();
        window.open(
          `https://wa.me/?text=${encodeURIComponent(dados.mensagem)}`,
          '_blank',
          'noopener,noreferrer',
        );
        setAviso('A imagem foi salva no seu celular — escolha ela no WhatsApp.');
      }
    } catch {
      // Usuário cancelou o compartilhamento: não bloqueia.
    } finally {
      setMandando(false);
    }
  }

  if (estado === 'semArte') return null;

  const pronto = estado === 'pronto' && !!blobUrl && !!dados;

  return (
    <div className="mt-5 border-t border-secondary-100 pt-4">
      {/* QR oculto, usado só para montar a cartela. */}
      {dados?.link && (
        <div className="hidden" aria-hidden="true">
          <QRCodeCanvas
            ref={qrRef}
            value={dados.link}
            size={512}
            level="M"
            fgColor="#182878"
            bgColor="#ffffff"
          />
        </div>
      )}

      {estado === 'preparando' && (
        <div className="flex h-40 items-center justify-center rounded-[14px] border border-secondary-200 bg-secondary-50">
          <span className="flex items-center gap-2 text-sm text-secondary-500">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-loader-4-line animate-spin" />
            </span>
            Preparando sua cartela…
          </span>
        </div>
      )}

      {estado === 'erro' && (
        <div className="rounded-[14px] border border-danger-200 bg-danger-50 p-4 text-center">
          <p className="text-sm font-medium text-danger-700">{erro}</p>
          <button
            type="button"
            onClick={tentarDeNovo}
            className="mt-3 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl border border-danger-300 bg-white px-4 py-2 text-sm font-semibold text-danger-700 transition-colors hover:bg-danger-50"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-refresh-line" />
            </span>
            Tentar de novo
          </button>
        </div>
      )}

      {pronto && blobUrl && (
        <img
          src={blobUrl}
          alt="Sua cartela com seu QR e seu nome"
          className="w-full rounded-[14px] border border-secondary-200 shadow-sm"
        />
      )}

      <p className="mt-2 text-[13px] text-secondary-500">Sua cartela com seu QR e seu nome.</p>

      <div className="mt-3 space-y-2">
        {pronto && blobUrl && dados ? (
          <a
            href={blobUrl}
            download={dados.arquivo || 'cartela.jpg'}
            className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-6 py-3 text-base font-bold text-white transition-colors hover:bg-primary-600"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-download-2-line" />
            </span>
            Baixar minha cartela
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="flex w-full cursor-not-allowed items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-300 px-6 py-3 text-base font-bold text-white"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-download-2-line" />
            </span>
            Preparando…
          </button>
        )}

        <button
          type="button"
          onClick={mandarWhats}
          disabled={!pronto || mandando}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-6 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-5 w-5 items-center justify-center text-[#25D366]">
            <i className="ri-whatsapp-line" />
          </span>
          {mandando ? 'Preparando…' : 'Mandar no WhatsApp'}
        </button>
      </div>

      {aviso && <p className="mt-2 text-center text-sm font-medium text-warning-700">{aviso}</p>}
    </div>
  );
}